import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance, onUnmounted } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import Dropzone from '@/components/dropzone/Dropzone.vue';
import ScannerCredentialFields from '@/_core/entities/scanner-credential-fields/ui/ScannerCredentialFields.vue';
import {
  activateCode,
  getAvailableScans,
  payForScans,
} from '@/_core/entities/scan/api/scan.endpoints';
import { useFetchAvailableScans } from '@/_core/entities/scan/api/useFetchAvailableScans';
import { useFetchScannerCredentialFields } from '@/_core/entities/scanner-credential-fields/api/useFetchScannerCredentialFields';
import type { TDropzoneFile, TScanFile } from '@/_core/entities/scan/models/types';

type TStage = 'upload' | 'credentials' | 'activate';


export default /*#__PURE__*/_defineComponent({
  __name: 'NewScanPage',
  setup(__props) {

const instance = getCurrentInstance();
const singleScanQueryPayload = computed(() => ({ mode: 'single-scan' as const }));
const { data: singleScanScannerCredentialFieldsData } =
  useFetchScannerCredentialFields(singleScanQueryPayload);

// Stage 1: Upload

const currentStage = ref<TStage>('upload');
const dropzoneRef = ref<{ removeFile: (file: TDropzoneFile) => void } | null>(null);
const allFiles = ref<TDropzoneFile[]>([]);
const isUploading = ref(false);
const showDropzone = ref(true);

const uploadedFiles = computed(() => allFiles.value.filter((f) => f.status === 'success'));
const trial = computed(() => instance?.proxy?.$store?.getters['user/getTrial']);
const singleScanScannerCredentialFields = computed(
  () => singleScanScannerCredentialFieldsData.value ?? []
);
const isFreeOrTrialScanFlow = computed(() => {
  if (!uploadedFiles.value.length) return false;

  return !!trial.value?.isTrial;
});
const shouldShowScannerCredentialFields = computed(
  () => uploadedFiles.value.length > 0 && !isFreeOrTrialScanFlow.value
);
const shouldUseScannerCredentialFieldsForScan = computed(() => {
  if (isFreeOrTrialScanFlow.value) return false;

  return singleScanScannerCredentialFields.value.some((field) => !!field?.value?.trim());
});

function onFilesAdded() {
  isUploading.value = true;
  showDropzone.value = false;
}

function onQueueComplete(files: TDropzoneFile[]) {
  const seen = new Set<string>();
  allFiles.value = files.filter((f) => {
    if (seen.has(f.name)) return false;
    seen.add(f.name);
    return true;
  });
  isUploading.value = false;
}

function removeFile(file: TDropzoneFile) {
  dropzoneRef.value?.removeFile(file);

  allFiles.value = allFiles.value.filter((f) => f.bucketKey !== file.bucketKey);

  if (allFiles.value.length !== 0) return;

  showDropzone.value = true;
}

function goToStage2() {
  if (shouldShowScannerCredentialFields.value) {
    currentStage.value = 'credentials';
    return;
  }

  currentStage.value = 'activate';
}

function goToStage3() {
  currentStage.value = 'activate';
}

function goBack() {
  if (currentStage.value === 'activate' && shouldShowScannerCredentialFields.value) {
    currentStage.value = 'credentials';
    return;
  }

  currentStage.value = 'upload';
}

// Stage 2: Activation & Scanning

const activationCodeInput = ref('');

const isCredentialsStage = computed(() => currentStage.value === 'credentials');
const isActivateStage = computed(() => currentStage.value === 'activate');
const { data: availableScansData, isLoading: isLoadingScans } =
  useFetchAvailableScans(isActivateStage);
const availableScansCount = computed(() => availableScansData.value ?? 0);

const canStartScanning = computed(
  () => uploadedFiles.value.length > 0 && availableScansCount.value >= uploadedFiles.value.length
);

const queryClient = useQueryClient();

let pollInterval: ReturnType<typeof setInterval> | null = null;

onUnmounted(() => {
  if (pollInterval !== null) clearInterval(pollInterval);
});

const POLLING_INTERVAL_MS = 1000;
const MAX_POLLING_ATTEMPTS = 20;

const isRedemptionTimeoutError = ref(false);

const {
  mutate: redeemCode,
  isPending: isRedeemingCode,
  isError: isActivationError,
} = useMutation({
  mutationFn: (code: string) => activateCode(code),
  onMutate: () => {
    isRedemptionTimeoutError.value = false;
  },
  onSuccess: async () => {
    const initialCount = availableScansCount.value;

    let attempts = 0;
    const hasCountIncreased = await new Promise<boolean>((resolve) => {
      pollInterval = setInterval(async () => {
        attempts++;
        try {
          const count = await getAvailableScans();
          if (count > initialCount) {
            clearInterval(pollInterval!);
            pollInterval = null;
            resolve(true);
            return;
          }
        } catch {
          /* retry on next tick */
        }

        if (attempts >= MAX_POLLING_ATTEMPTS) {
          clearInterval(pollInterval!);
          pollInterval = null;
          resolve(false);
        }
      }, POLLING_INTERVAL_MS);
    });

    if (!hasCountIncreased) {
      isRedemptionTimeoutError.value = true;
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['availableScans'] });
  },
});

const {
  mutate: startScanning,
  isPending: isStartingScans,
  isSuccess: isScanSuccess,
  error: scanMutationError,
} = useMutation({
  mutationFn: async () => {
    const files: TScanFile[] = uploadedFiles.value.map((f) => ({
      sourceBucketKey: f.bucketKey,
      fileName: f.name,
      platform: f.platform as 'android' | 'ios',
      jobType: 'PREPAID',
    }));

    const result = await payForScans({
      files,
      isNotifyByEmail: true,
      isUsePrepaidScans: true,
      isUseScannerCredField: shouldUseScannerCredentialFieldsForScan.value,
      scanPackId: null,
      isNewCard: false,
    });

    if (!result.paid) throw new Error('Payment failed');

    return result;
  },
  onSuccess: () => {
    instance?.proxy.$router.push('/scans');
  },
});

const scanError = computed(() => {
  if (!scanMutationError.value) return '';
  const axiosError = scanMutationError.value as { response?: { data?: string } };
  return axiosError?.response?.data || 'An error occurred. Please try again.';
});

return { __sfc: true,instance, singleScanQueryPayload, singleScanScannerCredentialFieldsData, currentStage, dropzoneRef, allFiles, isUploading, showDropzone, uploadedFiles, trial, singleScanScannerCredentialFields, isFreeOrTrialScanFlow, shouldShowScannerCredentialFields, shouldUseScannerCredentialFieldsForScan, onFilesAdded, onQueueComplete, removeFile, goToStage2, goToStage3, goBack, activationCodeInput, isCredentialsStage, isActivateStage, availableScansData, isLoadingScans, availableScansCount, canStartScanning, queryClient, pollInterval, POLLING_INTERVAL_MS, MAX_POLLING_ATTEMPTS, isRedemptionTimeoutError, redeemCode, isRedeemingCode, isActivationError, startScanning, isStartingScans, isScanSuccess, scanMutationError, scanError, Dropzone, ScannerCredentialFields }
}

})